import { useId, type ReactNode } from 'react';
import { CHANNEL_LABELS, normalizeChannel } from '@/calendar/channelMeta';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';

type Props = {
  channel: string;
  size?: number;
  title?: string;
  className?: string;
};

export function ConversationChannelIcon({ channel, size = 16, title, className }: Props) {
  const ch = normalizeChannel(channel);
  const label = title ?? CHANNEL_LABELS[ch] ?? ch;
  const s = size;
  const igGrad = useId();

  const wrap = (node: ReactNode) => (
    <span
      className={className}
      title={label}
      aria-label={label}
      role="img"
      style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}
    >
      {node}
    </span>
  );

  switch (ch) {
    case 'messenger':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <path
            fill="#1877F2"
            d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
          />
        </svg>
      );
    case 'whatsapp_js':
    case 'whatsapp_meta':
      return wrap(<WhatsAppIcon size={s} />);
    case 'instagram':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <defs>
            <radialGradient id={igGrad} cx="30%" cy="107%" r="150%">
              <stop offset="0%" stopColor="#fdf497" />
              <stop offset="45%" stopColor="#fd5949" />
              <stop offset="70%" stopColor="#d6249f" />
              <stop offset="100%" stopColor="#285AEB" />
            </radialGradient>
          </defs>
          <rect x="2" y="2" width="20" height="20" rx="5" fill={`url(#${igGrad})`} />
          <circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" strokeWidth="1.4" />
          <circle cx="17.4" cy="6.6" r="1.15" fill="#fff" />
        </svg>
      );
    case 'telegram':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="12" fill="#229ED9" />
          <path
            fill="#fff"
            transform="scale(0.55) translate(5.5,5.5)"
            d="M17.2 7.3L5.8 12.1c-.7.3-.7.7-.1.9l2.9.9 6.7-4.2c.4-.2.7-.1.4.1l-5.4 4.9-.2 3c.2 0 .3-.1.5-.2l2.4-2.3 5 3.7c.9.5 1.6.2 1.8-.7l1.8-8.5c.3-1.2-.5-1.7-1.3-1.4z"
          />
        </svg>
      );
    case 'web_widget':
    case 'web':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <rect x="3" y="5" width="18" height="14" rx="2" fill="#0ea5e9" />
          <path fill="#e0f2fe" d="M7 10h10v1.5H7zm0 3h7v1.5H7z" />
        </svg>
      );
    case 'slack':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <rect width="24" height="24" rx="4" fill="#4A154B" />
          <path fill="#E01E5A" d="M5 9.5a2.5 2.5 0 114.5 0 2.5 2.5 0 01-4.5 0zM9.5 5h5v5h-5V5z" />
          <path fill="#36C5F0" d="M14.5 5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM19 9.5v5h-5v-5h5z" />
          <path fill="#2EB67D" d="M14.5 14.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM9.5 19H5v-5h4.5v5z" />
          <path fill="#ECB22E" d="M5 14.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" />
        </svg>
      );
    case 'sms_twilio':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <rect x="2" y="4" width="20" height="16" rx="2" fill="#F22F46" />
          <path fill="#fff" d="M6 8h12v1.5H6zm0 3h9v1.5H6z" />
        </svg>
      );
    case 'email':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <rect x="2" y="4" width="20" height="16" rx="2" fill="#7c3aed" />
          <path fill="#ede9fe" d="M2 6l10 7 10-7v-2H2v2z" />
        </svg>
      );
    case 'teams':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <rect width="24" height="24" rx="4" fill="#5059C9" />
          <path fill="#fff" d="M14 8h4v8h-4V8zm-6 2h4v6H8v-6z" opacity="0.9" />
        </svg>
      );
    case 'testchat':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <rect width="24" height="24" rx="4" fill="#64748b" />
          <path fill="#fff" d="M7 8h10v1.5H7zm0 3h8v1.5H7zm0 3h6v1.5H7z" />
        </svg>
      );
    case 'demo':
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <rect width="24" height="24" rx="4" fill="#0d9488" />
          <path fill="#fff" d="M8 7l9 5-9 5V7z" />
        </svg>
      );
    default:
      return wrap(
        <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="10" fill="#94a3b8" />
          <path fill="#fff" d="M8 8h8v1.5H8zm0 3h6v1.5H8z" />
        </svg>
      );
  }
}
